import { Configuration, PublicClientApplication } from '@azure/msal-browser';
import { LogLevel } from '@azure/msal-common';
import { RootState } from 'Data/Redux';
import { actions } from 'Data/ReduxViewModels/Authentication';
import { hasEmptyValue } from 'Lib/Any';
import deepEquals from 'Lib/Object/deepEquals';
import { useEffect } from 'react';
import { isEdge } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';

const redirectUri = process.env.PUBLIC_URL;
const isDevMode = process.env.REACT_APP_ENABLE_DEV_MODE === 'true';

export let entraInstance: PublicClientApplication;

export function useEntraAuthentication() {
  InitialiseEntraAuthentication();
}

export function InitialiseEntraAuthentication() {
  const isEntraInitialised = useSelector((state: RootState) => state.authentication.entra.isInitialised);
  const settings = useSelector((state: RootState) => state.authentication.authSettings, deepEquals);
  const dispatch = useDispatch();

  useEffect(() => {
    if (hasEmptyValue(settings)) {
      dispatch(actions.getSettings());
    }
  }, [dispatch, settings]);

  useEffect(() => {
    if (!isEntraInitialised && settings && settings.isEntraEnabled) {
      if (settings.clientId === undefined || settings.tenantId === undefined) {
        dispatch(
          actions.initialiseEntraReject({
            severity: 'error',
            message: 'Microsoft Entra Authentication is enabled but the client ID or tenant ID is null.',
          })
        );
        return;
      }
      dispatch(actions.initialiseEntra());

      entraInstance = new PublicClientApplication(getConfig(settings?.clientId, settings?.tenantId));
      entraInstance
        .initialize()
        .then(() => {
          //This is specifically for the QF App where we use the redirect flow to get the token.
          if (window.qest.userAgent?.includes('[QF.App]'))
            entraInstance
              .handleRedirectPromise()
              .then((resp: any) => {
                dispatch(actions.initialiseEntraResolve());
                if (resp !== null) {
                  entraInstance.setActiveAccount(resp.account);
                  dispatch(actions.postLogin({ token: resp.accessToken }));
                  dispatch(actions.initialiseEntraResolve());
                }
              })
              .catch((err: any) => {
                dispatch(actions.initialiseEntraReject({ severity: 'error', message: err.errorMessage }));
              });
          else dispatch(actions.initialiseEntraResolve());
        })
        .catch((err: any) => dispatch(actions.initialiseEntraReject({ severity: 'error', message: err.errorMessage })));
    }
  }, [dispatch, isEntraInitialised, settings]);
}

export function acquireAccessTokenRedirect(clientId: string) {
  return entraInstance.acquireTokenRedirect(getRequest(clientId!));
}

export function acquireAccessToken(clientId: string) {
  const request = getRequest(clientId);
  //For the QF.App we only support the redirect flow as the embedded webview does not support popups.
  if (window.qest.userAgent?.includes('[QF.App]')) return entraInstance.acquireTokenRedirect(request);
  //For QL we do not support acquiring tokens silently as we need to allow the user to logout and select a different account. We support SSO in QL
  //via windows based authentication in the Com.Authentication library.
  if (window.qest.userAgent?.includes('[QL]')) return entraInstance.acquireTokenPopup(request);
  return entraInstance.acquireTokenSilent(request).catch(() => entraInstance.acquireTokenPopup(request));
}

export function entraLogout() {
  return entraInstance.logoutPopup({ account: getActiveAccount() });
}

function getActiveAccount() {
  const activeAccount = entraInstance.getActiveAccount();
  if (activeAccount) return activeAccount;

  const accounts = entraInstance.getAllAccounts();
  if (accounts.length > 0) {
    entraInstance.setActiveAccount(accounts[0]);
    return accounts[0];
  }
}

function getRequest(clientId: string) {
  return {
    scopes: [`api://${clientId}/access_as_user`],
    account: getActiveAccount(),
    prompt: window.qest.userAgent?.includes('[QL]') ? 'login' : '',
  };
}

function getConfig(clientId: string | undefined, tenantId: string | undefined) {
  return {
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: redirectUri,
      postLogoutRedirectUri: redirectUri,
    },
    cache: {
      cacheLocation: 'localStorage',
    },
    system: {
      allowRedirectInIframe: true, //This is required for the redirect flow to work in iframes
      loggerOptions: {
        logLevel: isDevMode ? LogLevel.Verbose : LogLevel.Error,
        loggerCallback: (level: any, message: any, containsPii: any) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Verbose:
              console.debug(message);
              return;
            case LogLevel.Trace:
              console.trace(message);
              return;
          }
        },
        piiLoggingEnabled: false,
      },

      //Native Broker (windows authentication) is broken in Chrome: https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/6169
      allowNativeBroker: isEdge,
    },
  } as Configuration;
}
